import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AuthContext } from '@/context/auth-context'
import { getMemberDisplayName, kUnavailableMemberDisplayName } from '@/collaboration/utils/members'
import { isDraftsDestinationEnabled } from '@/drafting/destination/drafts-destination-flag'
import {
  selectMatterArtifactDraftRows,
  selectMatterArtifactDraftsFetchFailedForMatter,
  selectMatterArtifactDraftsEverLoadedForMatter,
  selectMatterArtifactDraftsInitialLoadCompleteForMatter,
  selectMatterArtifactDraftsLastFetchFinishedAtForMatter,
  selectMatterArtifactDraftsLoadingForMatter,
  selectMatterArtifactDraftsStaleForMatter,
  type MatterArtifactDraftRow,
} from '@/drafting/store/selectors'
import { DraftingActions } from '@/drafting/store/slice'
import { selectActiveMatterId, selectMatterMembers, selectMatterMembersInitialLoadComplete } from '@/matter/store/selectors'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'

/** Creator label shown on a draft the current user created. */
export const kOwnDraftCreatorLabel = 'Me'

/** A Drafts destination row with its creator resolved for display. */
export interface MatterArtifactDraftListRow extends MatterArtifactDraftRow {
  /**
   * The creator's name, or `Me` on the current user's own drafts.
   *
   * Null only while the matter's members have not loaded yet, so a row shows a blank cell for that
   * moment rather than briefly claiming the creator is unavailable. Once they have loaded, a
   * creator who is no longer a member of the matter reads as the shared unavailable-user label;
   * whether such a draft should instead keep showing its original author is an open product
   * question, tracked on the ticket.
   */
  creatorLabel: string | null
  /**
   * Whether the current user created this draft.
   *
   * Exports that could differ from what the user sees on screen — a copy saved into matter files,
   * which the creator may have templated — belong to the creator alone, matching the draft view.
   */
  isCreatedByCurrentUser: boolean
}

export interface UseMatterArtifactDraftsResult {
  matterId: string | null
  rows: MatterArtifactDraftListRow[]
  /**
   * True when the list could not be loaded and there is no result to stand in for it: nothing has
   * ever loaded for this matter, or the cached result is empty and no fetch has succeeded since the
   * page was entered.
   */
  isError: boolean
  /** True while the list can't be shown yet: loading or not-yet-loaded, with nothing cached to fall back on. */
  isLoadingWithoutCachedDrafts: boolean
  /** Force a refetch of the route matter's drafts (e.g. Retry after an error). */
  refetch: () => void
}

/**
 * The route matter's drafts, as rows for the Drafts destination.
 * The Matter Overview keeps its own request and cache.
 *
 * Gated on the destination's own flag check so no destination-owned request runs while the
 * destination is off, even if a caller mounts the hook.
 */
export function useMatterArtifactDrafts(): UseMatterArtifactDraftsResult {
  const dispatch = useAppDispatch()
  const { user } = useContext(AuthContext)
  const destinationEnabled = isDraftsDestinationEnabled()

  // The URL owns which matter this is; Redux only catches up to it. MatterLayout syncs the two in an
  // effect, and a child's effects run before its parent's, so a client-side move between two
  // matters' Drafts pages — browser back/forward included — has a frame where the active matter is
  // still the one the user came from. Treating that disagreement as "not resolved yet" keeps the
  // page from painting, fetching, or opening a draft for the wrong matter. With no `:matterId` in
  // the route there is no URL claim to disagree with, so the active matter stands on its own.
  const { matterId: routeMatterId } = useParams<{ matterId: string }>()
  const activeMatterId = useAppSelector((state: RootState) => selectActiveMatterId(state))
  const matterId = routeMatterId && routeMatterId !== activeMatterId ? null : activeMatterId
  const draftRows = useAppSelector((state: RootState) => selectMatterArtifactDraftRows(state, { matterId }))
  const isLoading = useAppSelector((state: RootState) => selectMatterArtifactDraftsLoadingForMatter(state, { matterId }))
  const fetchFailed = useAppSelector((state: RootState) => selectMatterArtifactDraftsFetchFailedForMatter(state, { matterId }))
  const initialLoadComplete = useAppSelector((state: RootState) => selectMatterArtifactDraftsInitialLoadCompleteForMatter(state, { matterId }))
  const everLoaded = useAppSelector((state: RootState) => selectMatterArtifactDraftsEverLoadedForMatter(state, { matterId }))
  const draftsStale = useAppSelector((state: RootState) => selectMatterArtifactDraftsStaleForMatter(state, { matterId }))
  const lastFetchFinishedAt = useAppSelector((state: RootState) => selectMatterArtifactDraftsLastFetchFinishedAtForMatter(state, { matterId }))
  const members = useAppSelector((state: RootState) => selectMatterMembers(state, matterId ?? undefined))
  const membersLoaded = useAppSelector((state: RootState) => selectMatterMembersInitialLoadComplete(state, matterId ?? undefined))

  // Fetch on mount/matter change, and again when the drafts go stale while mounted (draftsStale), so the
  // view updates live.
  //
  // Entering the page bypasses the cache rather than waiting for it to expire. Only changes made in
  // this tab mark the list stale, so anything another member of the matter does — deleting the
  // conversation behind a draft, saving an edit, renaming one — leaves this tab's rows wrong with
  // nothing to signal it, and a row for a deleted draft opens a page that no longer exists. Arriving
  // at the list is the moment the user expects it to be right. The already-cached rows stay on
  // screen while the refetch runs, so a revisit shows the list rather than skeletons. Subsequent
  // runs while mounted are not forced, so the completed fetch that clears `draftsStale` cannot
  // trigger another one.
  const forcedMatterIdRef = useRef<string | null>(null)
  const entryFetchFinishedAtRef = useRef<number | null>(null)
  const [confirmedMatterId, setConfirmedMatterId] = useState<string | null>(null)
  useEffect(() => {
    if (!destinationEnabled || !matterId) return
    const alreadyRevalidated = forcedMatterIdRef.current === matterId
    forcedMatterIdRef.current = matterId
    if (!alreadyRevalidated) {
      entryFetchFinishedAtRef.current = lastFetchFinishedAt
      setConfirmedMatterId(null)
    }
    dispatch(DraftingActions.refreshMatterArtifactDrafts({ matterId, forceRefresh: !alreadyRevalidated }))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lastFetchFinishedAt only marks where this visit starts; a fetch settling is no reason to fetch again
  }, [destinationEnabled, matterId, draftsStale, dispatch])

  // Whether a fetch has succeeded since the page was entered. Sticky for the visit, so a poll that
  // fails after it cannot take it back.
  useEffect(() => {
    if (!matterId || forcedMatterIdRef.current !== matterId) return
    const settledSinceEntry = lastFetchFinishedAt !== null && lastFetchFinishedAt !== entryFetchFinishedAtRef.current
    if (settledSinceEntry && !fetchFailed) setConfirmedMatterId(matterId)
  }, [matterId, lastFetchFinishedAt, fetchFailed])

  const refetch = useCallback(() => {
    if (!destinationEnabled || !matterId) return
    dispatch(DraftingActions.refreshMatterArtifactDrafts({ matterId, forceRefresh: true }))
  }, [destinationEnabled, matterId, dispatch])

  // A cold load that fails gets the error card and its Retry, since there is nothing else to show.
  // Cached rows stand in for a failed refresh, since they were right once.
  // A cached empty list only stands in once this visit has confirmed it. Before that, the empty-matter
  // card would claim there are no drafts when the list is unknown, and a personal account has no
  // poll to correct it. After it, a failed poll says nothing, since the poll makes a background
  // failure an ordinary event.
  const isError = fetchFailed && (!everLoaded || (draftRows.length === 0 && confirmedMatterId !== matterId))

  // Creator names resolve from state the page already has — no source chat is fetched or hydrated to
  // identify a creator. MatterLayout loads the matter's members on entry for every account, and a
  // creator who is no longer a member resolves to the unavailable-user label once they have loaded.
  const currentUserId = user?.uid
  const memberNamesById = useMemo(() => new Map(members.map((member) => [member.user_id, getMemberDisplayName(member)])), [members])
  const rows = useMemo(
    () =>
      draftRows.map((row) => ({
        ...row,
        creatorLabel: resolveCreatorLabel({ createdByUserId: row.createdByUserId, currentUserId, memberNamesById, membersLoaded }),
        isCreatedByCurrentUser: row.createdByUserId === currentUserId,
      })),
    [draftRows, currentUserId, memberNamesById, membersLoaded]
  )

  // No rows to show yet and a load is happening, still pending, or not yet addressable. An unknown
  // matter counts as pending: MatterLayout sets the active matter in an effect, and a child's effects
  // run before its parent's, so this page's first painted frame on a direct visit has no matter yet.
  // Reporting "loaded and empty" there paints the "Create your first draft" card before the skeletons.
  // Still false with the flag off, so a caller never sits on a permanent loading state.
  //
  // One successful fetch ends it for good (`everLoaded`). Its result stands, rows or the empty-matter
  // card, and every refresh after it runs underneath. Without that, a matter with no drafts would
  // swap its empty card for skeletons on entry and on each poll, because no rows is also what
  // "nothing loaded yet" looks like. A retry after a failed first load still shows them: nothing has
  // succeeded, so there is nothing on screen to protect.
  const isLoadingWithoutCachedDrafts = destinationEnabled && rows.length === 0 && !everLoaded && (matterId === null || isLoading || !initialLoadComplete)

  return { matterId, rows, isError, isLoadingWithoutCachedDrafts, refetch }
}

/**
 * A draft's Created By label.
 *
 * The members fetch and the drafts fetch are dispatched on the same mount and race, so an
 * unresolved id means two different things at two different moments: before the members land it
 * means "not known yet" (blank, and it fills in), and after they land it means the creator has left
 * the matter (the shared unavailable-user label). Collapsing the two would make every collaborator
 * row flash "Unavailable user" on a direct page visit whenever drafts resolve first.
 */
function resolveCreatorLabel({
  createdByUserId,
  currentUserId,
  memberNamesById,
  membersLoaded,
}: {
  createdByUserId: string
  currentUserId: string | undefined
  memberNamesById: Map<string, string>
  membersLoaded: boolean
}): string | null {
  if (createdByUserId === currentUserId) return kOwnDraftCreatorLabel

  const memberName = memberNamesById.get(createdByUserId)
  if (memberName) return memberName

  return membersLoaded ? kUnavailableMemberDisplayName : null
}
